// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useCreateForumMutation } from "messaging/_api/messaging-api";
import { getMessagingErrorMessage, getMessagingFieldErrors } from "messaging/_api/messaging-error-message";
import Forum from "messaging/forums/forum";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";
// #endregion

// #region Styled Components
const ForumContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  margin: theme.spacing(2, 0),
  width: "100%",
}));
const AddSubscriberContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
  },
  minWidth: theme.spacing(40),
  paddingLeft: theme.spacing(8),
  textAlign: "left",
}));
const ForumNameCreate = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100%",
    "&:first-of-type": {
      marginBottom: theme.spacing(1.5),
      marginRight: 0,
    },
  },
  minWidth: theme.spacing(40),
}));
const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  padding: theme.spacing(4),
  paddingTop: 0,
  width: "100%",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const ConnectionListContainer = styled(List)(({ theme }) => ({
  maxHeight: "20rem",
  overflowY: "auto",
  padding: theme.spacing(1, 0),
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
}));
// #endregion

// #region Types & Props
type CreateForumDialogProps = {
  onForumCreated: (createdForum: Forum) => void;
  onCancel: () => void;
};

export default function CreateForumDialog(props: Readonly<CreateForumDialogProps>) {
  const { onForumCreated, onCancel } = props;

  // #region State
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [forumNameValue, setForumNameValue] = useState<string>("");
  const [subscriberUserIds, setSubscriberUserIds] = useState<string[]>([]);
  const [erroredUserIds, setErroredUserIds] = useState<string[]>([]);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const createOperationId = useRef(Guid.generate());

  // #region Hooks
  const session = useSession();
  const [createForum] = useCreateForumMutation();
  const networkConnectionQuery = useGetUserNetworkConnectionsQuery(
    debouncedSearchTerm ? { query: debouncedSearchTerm } : undefined,
    { refetchOnMountOrArgChange: true }
  );

  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data);
  }, [networkConnectionQuery.data]);

  // #region Functions
  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    const debounceHandle = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, trimmedSearchTerm ? 300 : 0);

    return () => globalThis.clearTimeout(debounceHandle);
  }, [searchTerm]);

  async function saveChanges(): Promise<void> {
    if (!session.user?.id || !session.user?.legalEntityId) return;
    if (!forumNameValue.trim()) {
      setFieldError("Conversation name is required.");
      return;
    }
    if (subscriberUserIds.length < 1) {
      setFieldError("Select at least one subscriber.");
      return;
    }
    setFieldError(undefined);

    setIsSaving(true);
    try {
      const subscribers: EntityMember[] = [];
      const missingUserIds: string[] = [];

      for (const userId of subscriberUserIds) {
        const connection = networkConnections.find((networkConnection) => networkConnection.id?.value === userId);
        if (!connection?.id || !connection.legalEntityId) {
          missingUserIds.push(userId);
          continue;
        }
        subscribers.push(
          EntityMember.create({
            userId: connection.id,
            legalEntityId: connection.legalEntityId,
          })
        );
      }

      if (missingUserIds.length > 0) {
        setErroredUserIds(missingUserIds);
        setFieldError("Unable to load some selected subscribers. Remove them and try again.");
        enqueueSnackbar(
          "Unable to find information for some selected subscribers. Please remove them and try again.",
          { variant: "warning" }
        );
        return;
      }

      const newForum = Forum.create({
        name: new ForumName(forumNameValue.trim()),
        subscribers: subscribers,
        createdDate: new AHDate(),
        creator: EntityMember.create({
          userId: session.user.id,
          legalEntityId: session.user.legalEntityId,
        }),
        numUnreadMessages: 0,
        topic: Topic.create({
          entityClass: new ForumEntityClass("userCreatedChat"),
          context: "UserCreatedChat",
        }),
      });
      const createdForum = Forum.fromJSON(
        await createForum({ forum: newForum, operationId: createOperationId.current }).unwrap()
      );
      if (!createdForum) throw new Error("Created forum response was invalid");
      onForumCreated(createdForum);
    } catch (error) {
      const fieldErrors = getMessagingFieldErrors(error);
      setFieldError(fieldErrors.name ?? fieldErrors.topic ?? fieldErrors.subscribers);
      enqueueSnackbar(getMessagingErrorMessage(error, "We could not create the conversation. Please try again."), { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  function handleSubscriberSelected(
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    subscriberUserId: string
  ): void {
    const selectedSubscriberIds = [...subscriberUserIds];
    const index = selectedSubscriberIds.indexOf(subscriberUserId);
    if (index === -1) {
      selectedSubscriberIds.push(subscriberUserId);
    } else {
      selectedSubscriberIds.splice(index, 1);
    }
    setSubscriberUserIds(selectedSubscriberIds);
  }

  function isSelected(subscriberUserId: string): boolean {
    return subscriberUserIds.includes(subscriberUserId);
  }

  // #region Variables
  const saveDisabled: boolean =
    subscriberUserIds.length < 1 ||
    !networkConnections.length ||
    isSaving ||
    !forumNameValue.trim() ||
    !session.user?.id ||
    !session.user?.legalEntityId;
  const isSearching = searchTerm.trim().length > 0 || debouncedSearchTerm.trim().length > 0;
  const isInitialLoading = networkConnectionQuery.isLoading && !networkConnectionQuery.data && !isSearching;
  const canCreateForum: boolean = networkConnections.length > 0 || isSearching;

  // #region Render
  return (
    <React.Fragment>
      {isInitialLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!isInitialLoading && !canCreateForum && (
        <LoaderContainer>
          <Typography>You are not networked with anyone</Typography>
        </LoaderContainer>
      )}
      {!isInitialLoading && canCreateForum && (
        <>
          <ForumContainer>
            <ForumNameCreate
              label="Conversation name"
              required={true}
              onChange={(e) => {
                setForumNameValue(e.target.value);
                if (e.target.value.trim()) setFieldError(undefined);
              }}
              value={forumNameValue}
              error={Boolean(fieldError && !forumNameValue.trim())}
              helperText={fieldError && !forumNameValue.trim() ? fieldError : "required"}
            />
            <AddSubscriberContainer>
              <SearchField
                size="small"
                variant="standard"
                placeholder="Search Connections"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="disabled" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {searchTerm.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm("");
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </>
                    ),
                  },
                }}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <ConnectionListContainer dense>
                {networkConnections.length < 1 && isSearching && (
                  <ListItem>
                    <ListItemText
                      primary="No matching connections"
                      slotProps={{ primary: { sx: { textAlign: "center", padding: 1, typography: "body1" } } }}
                    />
                  </ListItem>
                )}
                {networkConnections.map((connection: UserInformation) => {
                  const hasError = erroredUserIds.includes(connection.id?.value ?? "");
                  return (
                    <ListItemButton
                      disableGutters
                      key={connection.id?.value}
                      selected={isSelected(connection.id.value)}
                      onClick={(event) => handleSubscriberSelected(event, connection.id.value)}
                    >
                      <ListItemAvatar>
                        {isSelected(connection.id.value) ? (
                          <SelectedIcon>
                            <DoneIcon />
                          </SelectedIcon>
                        ) : (
                          <IndividualAvatar individual={connection.toIndividual()} />
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          hasError ? `${connection.name.fullName} (Error Loading)` : connection.name.fullName
                        }
                        secondary={connection.email?.value ?? ""}
                        slotProps={{
                          primary: {
                            color: hasError ? "error" : "textPrimary",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </ConnectionListContainer>
            </AddSubscriberContainer>
          </ForumContainer>
          {fieldError && forumNameValue.trim() && <Typography color="error">{fieldError}</Typography>}
          <ButtonContainer>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              loading={isSaving}
              disabled={saveDisabled}
              onClick={saveChanges}
            >
              Save
            </LoadingButton>
            <Button variant="contained" color="primary" onClick={onCancel}>
              Cancel
            </Button>
          </ButtonContainer>
        </>
      )}
    </React.Fragment>
  );
}
